import type * as React from 'react';

import { Box } from '../../components/ui/box';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/icon';
import { Text } from '../../components/ui/text';
import {
  Menu,
  MenuTrigger,
  MenuPopup,
  MenuGroup,
  MenuGroupLabel,
  MenuSeparator,
  MenuItem,
} from '../../components/ui/menu';
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from '../../components/ui/tooltip';

import type { DataTableGroupActionsContext } from './data-table-types';
import type { Omittable } from "../../lib/props";

export type DataTableGroupAddAction = Omittable<{
  /** Tooltip + accessibility label, e.g. "Add Project" / "Add tag". */
  label: string;
  onClick: () => void;
}>;

/**
 * The "+" quick-add sitting right of the group's "…" menu — same create flow
 * as a group-footer "+ Add …" row, surfaced in the header.
 */
function DataTableGroupAddButton({ label, onClick }: DataTableGroupAddAction) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={label}
            onClick={onClick}
          />
        }
      >
        <Icon name="plus" />
      </TooltipTrigger>
      <TooltipPopup side="top">{label}</TooltipPopup>
    </Tooltip>
  );
}

/**
 * Default group actions dropdown shared across workspace tables.
 *
 * Renders a menu with: Collapse group, Select all, Expand/Collapse all groups.
 * Callers can add surface-specific items in two slots: `prepend` renders as
 * the first section under the heading (edit actions on the entity the group
 * IS, e.g. "Rename"), `append` in its own section below the shared items
 * (e.g. "Automate stage"). A "+" quick-add button right of the menu comes via
 * `addAction`. Both header triggers carry hover tooltips (touch never opens
 * them, so no gate). `menuTriggerRef` lands on the wrapper around the "…" so
 * a caller can hang a follow-on popup off the same corner after a row closes
 * this menu — the kanban column header offers the same handle.
 */
export function renderDefaultGroupActions<TData>({
  rows,
  isCollapsed,
  isGroupSelected,
  collapseGroup,
  collapseAll,
  expandAll,
  allCollapsed,
  selectAll,
  deselectAll,
  prepend,
  append,
  addAction,
  menuTriggerRef,
}: DataTableGroupActionsContext<TData> & Omittable<{
  prepend?: React.ReactNode;
  append?: React.ReactNode;
  addAction?: DataTableGroupAddAction;
  /** Receives the element wrapping the "…" trigger: the anchor for a popup a
   *  row of this menu opens in its place. */
  menuTriggerRef?: React.Ref<HTMLDivElement>;
}>): React.ReactNode {
  return (
    <>
      {/* The wrapper carries the anchor ref, not the Button: it hugs the "…"
          exactly and survives the trigger being composed through two `render`
          props. */}
      <Box ref={menuTriggerRef} direction="row" align="center">
      <Menu>
        <Tooltip>
          <MenuTrigger
            render={
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Group options"
                  />
                }
              />
            }
          >
            <Icon name="ellipsis" />
          </MenuTrigger>
          <TooltipPopup side="top">Group options</TooltipPopup>
        </Tooltip>
        {/* The browsed header-anchored menu shape (matches the kanban column's
            Group options menu): panel width, roomy inset, and the label runs
            straight into its first section — separators divide sections only. */}
        <MenuPopup align="start" width="panel">
          {/* MenuGroupLabel resolves its labelled set through MenuGroup context
              and throws without one, so the heading and the actions it names are
              one group. `append` stays outside — it is the caller's own set. */}
          <MenuGroup>
            <MenuGroupLabel>Group options</MenuGroupLabel>
            {/* Caller items on the group's OWN entity lead — the reference
                menus put Rename/Edit above the collapse/select commands. */}
            {prepend ? (
              <>
                {prepend}
                <MenuSeparator />
              </>
            ) : null}
            <MenuItem onClick={collapseGroup}>
              <Icon name={isCollapsed ? 'chevrons-down' : 'chevrons-up'} size="lg" color="muted" />
              <Text size="sm">{isCollapsed ? 'Expand group' : 'Collapse group'}</Text>
            </MenuItem>
            <MenuSeparator />
            {/* An empty group has nothing to select — the item would no-op. */}
            {rows.length > 0 ? (
              <MenuItem onClick={isGroupSelected ? deselectAll : selectAll}>
                <Icon name="list-checks" size="lg" color="muted" />
                <Text size="sm">{isGroupSelected ? 'Deselect all' : 'Select all'}</Text>
              </MenuItem>
            ) : null}
            <MenuItem onClick={allCollapsed ? expandAll : collapseAll}>
              <Icon
                name={allCollapsed ? 'chevrons-down' : 'chevrons-up'}
                size="lg"
                color="muted"
              />
              <Text size="sm">
                {allCollapsed ? 'Expand all groups' : 'Collapse all groups'}
              </Text>
            </MenuItem>
          </MenuGroup>
          {append ? (
            <>
              <MenuSeparator />
              {append}
            </>
          ) : null}
        </MenuPopup>
      </Menu>
      </Box>
      {addAction ? <DataTableGroupAddButton {...addAction} /> : null}
    </>
  );
}
